import React from 'react';
import Money from './Money';
import { appLocale } from '../../utils/locale';

// Amount tiles read as balances, not quantities: grouped thousands and two
// decimals, always, so 8394 renders 8,394.00 beside the riyal mark.
const formatAmount = (value) =>
  Number(value || 0).toLocaleString(appLocale('en'), { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// Soft icon-chip tints for the toned circle — same grammar as StatCard/Dashboard
// "needs attention" tiles. Only the tones the band offers live here.
const TINTS = {
  amber: 'bg-amber-50 text-amber-600 dark:bg-amber-500/10 dark:text-amber-400',
  red: 'bg-red-50 text-red-600 dark:bg-red-500/10 dark:text-red-400',
  blue: 'bg-blue-50 text-blue-600 dark:bg-blue-500/10 dark:text-blue-400',
  green: 'bg-green-50 text-green-600 dark:bg-green-500/10 dark:text-green-400',
  accent: 'bg-accent-soft text-accent',
};

// Horizontal "needs attention" leads band above list pages. Presentational:
// all text (labels) arrives via props. Items with count<=0 are dropped; when
// none survive the whole band collapses (renders nothing).
export default function AttentionBand({ items = [], className = '' }) {
  const visible = items.filter((it) => Number(it.count) > 0);
  if (visible.length === 0) return null;

  return (
    <div className={`flex items-stretch gap-2 overflow-x-auto pb-1 ${className}`}>
      {visible.map(({ key, icon: Icon, label, count, money, tone = 'amber', onClick }) => {
        const Comp = onClick ? 'button' : 'div';
        return (
          <Comp
            key={key}
            onClick={onClick}
            type={onClick ? 'button' : undefined}
            className={`inline-flex items-center gap-3 shrink-0 min-h-[44px] px-3 py-2 rounded-xl border border-edge bg-surface text-start ${
              onClick
                ? 'transition-colors hover:border-edge-strong hover:bg-surface-2 cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
                : ''
            }`}
          >
            <span className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${TINTS[tone] || TINTS.amber}`}>
              {Icon && <Icon size={18} />}
            </span>
            <span className="min-w-0">
              {/* `count` is always the raw number so the filter above works.
                  `money: true` is what an amount tile sets — a bare rounded
                  integer read as a quantity, not a balance. */}
              <span className="block text-sm font-bold text-content tabular-nums leading-tight" dir="ltr">
                {money ? <Money>{formatAmount(count)}</Money> : count}
              </span>
              <span className="block text-xs text-content-muted truncate">{label}</span>
            </span>
          </Comp>
        );
      })}
    </div>
  );
}
